import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BBALoader from '../../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { deleteBid, fetchAllBids } from '../../services/alaCarteBidService';
import { getAccounts, getCrmCompanyLogoDisplayUrl } from '../../services/firestoreService';
import {
  PRICING_STATUS,
  BID_STATUS_LABELS,
  CLIENT_RESPONSE_STATUS,
  CLIENT_RESPONSE_LABELS,
  INTERNAL_BID_COMPANY,
  bidAccountTypeLabel,
  isInternalBid,
} from '../../utils/alaCarteBid';
import { computePricingMetrics } from '../../utils/alaCarteCatalog';
import {
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Clock,
  Package,
  Search,
  Send,
  ThumbsUp,
  Trash2,
  X,
} from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';
import AddButton from '../../components/AddButton';
import CrmStatCard from '../../components/crm/CrmStatCard';
import NewBidModal from '../../components/crm/NewBidModal';

const DEPARTMENT_FILTERS = ['All', 'Finance', 'Marketing', 'HR'].map((d) => ({ value: d, label: d }));

const ACCOUNT_TYPE_FILTERS = [
  { value: 'All', label: 'All' },
  { value: 'Clients', label: 'Clients' },
  { value: 'Leads', label: 'Leads' },
  { value: 'Internal', label: 'Internal' },
];

const STATUS_FILTERS = [
  { value: 'All', label: 'All' },
  { value: PRICING_STATUS.awaiting_team, label: BID_STATUS_LABELS[PRICING_STATUS.awaiting_team] },
  { value: PRICING_STATUS.team_submitted, label: BID_STATUS_LABELS[PRICING_STATUS.team_submitted] },
  { value: PRICING_STATUS.exec_ready, label: BID_STATUS_LABELS[PRICING_STATUS.exec_ready] },
  { value: PRICING_STATUS.published, label: BID_STATUS_LABELS[PRICING_STATUS.published] },
  { value: PRICING_STATUS.draft, label: BID_STATUS_LABELS[PRICING_STATUS.draft] },
];

function contactDisplayName(account) {
  const c = account?.contact || {};
  const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
  return full || c.name || '';
}

/** Out: sent to the team, no pricing back yet. */
function isBidOut(bid) {
  return bid.pricingStatus === PRICING_STATUS.awaiting_team;
}

/** In: the team has put a number on it (submitted, in exec review, or published). */
function isBidIn(bid) {
  return (
    bid.pricingStatus === PRICING_STATUS.team_submitted ||
    bid.pricingStatus === PRICING_STATUS.exec_ready ||
    bid.pricingStatus === PRICING_STATUS.published
  );
}

function formatMoney(raw) {
  const n = parseFloat(String(raw));
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  if (!value) return '—';
  if (typeof value.toDate === 'function') return value.toDate().toLocaleDateString();
  if (value instanceof Date) return value.toLocaleDateString();
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleDateString() : '—';
}

function statusPillClass(status) {
  switch (status) {
    case PRICING_STATUS.published:
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case PRICING_STATUS.awaiting_team:
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100';
    case PRICING_STATUS.team_submitted:
    case PRICING_STATUS.exec_ready:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

function clientResponsePillClass(status) {
  switch (status) {
    case CLIENT_RESPONSE_STATUS.accepted:
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case CLIENT_RESPONSE_STATUS.declined:
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    default:
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100';
  }
}

/** Logo in a circle, falling back to the company's first letter (no logo uploaded, or it fails to load). */
function CompanyLogo({ logoUrl, name, size = 'h-12 w-12' }) {
  return (
    <div
      className={`${size} flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-700`}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="max-h-[88%] max-w-[88%] object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            const next = e.target.nextSibling;
            if (next) next.style.display = 'block';
          }}
        />
      ) : null}
      <span
        className="text-lg font-semibold text-gray-500 dark:text-gray-300"
        style={{ display: logoUrl ? 'none' : 'block' }}
      >
        {(name?.trim()?.[0] || '?').toUpperCase()}
      </span>
    </div>
  );
}

/**
 * Single-select dropdown styled like the CRM Tasks filters: "Label: Value" pill button,
 * clear X when not on the default, menu card below.
 */
function TaskStyleFilter({ label, value, options, onChange, defaultValue = 'All' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const current = options.find((opt) => opt.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        {label}: {current?.label ?? value}
        {value !== defaultValue && (
          <X
            size={14}
            className="ml-1 hover:text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              onChange(defaultValue);
            }}
          />
        )}
        <ChevronDown size={16} />
      </button>
      {open && (
        <div className="absolute mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-20 right-0 overflow-hidden w-52">
          <div className="p-2">
            {options.map((opt) => (
              <div
                key={opt.value}
                className={`p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${
                  opt.value === value ? 'font-semibold' : ''
                }`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <span className="text-gray-900 dark:text-white">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AlaCarteBidsBoard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bids, setBids] = useState([]);
  const [accountsById, setAccountsById] = useState(new Map());
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [accountTypeFilter, setAccountTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [showNewBid, setShowNewBid] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [bidList, accounts] = await Promise.all([fetchAllBids(), getAccounts()]);
      setBids(bidList);
      setAccountsById(new Map(accounts.map((a) => [a.id, a])));
    } catch (e) {
      console.error(e);
      setBids([]);
      setAccountsById(new Map());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedGroupKey) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedGroupKey(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedGroupKey]);

  useEffect(() => {
    setConfirmDeleteId('');
  }, [selectedGroupKey]);

  const handleDeleteBid = async (bid) => {
    if (deletingId) return;
    setDeletingId(bid.id);
    try {
      await deleteBid(bid);
      setBids((prev) => prev.filter((b) => b.id !== bid.id));
      setConfirmDeleteId('');
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to delete the bid');
    } finally {
      setDeletingId('');
    }
  };

  const filtered = useMemo(() => {
    let list = bids;
    if (departmentFilter !== 'All') list = list.filter((b) => b.department === departmentFilter);
    if (accountTypeFilter !== 'All') list = list.filter((b) => b.accountType === accountTypeFilter);
    if (statusFilter !== 'All') list = list.filter((b) => b.pricingStatus === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (b) => (b.companyName || '').toLowerCase().includes(q) || (b.name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [bids, departmentFilter, accountTypeFilter, statusFilter, search]);

  /** All-departments view: one card per account. */
  const accountGroups = useMemo(() => {
    const map = new Map();
    filtered.forEach((bid) => {
      const key = bid.accountId || `unknown-${bid.id}`;
      if (!map.has(key)) {
        const internal = isInternalBid(bid);
        const account = bid.accountId && !internal ? accountsById.get(bid.accountId) : null;
        map.set(key, {
          key,
          accountId: bid.accountId,
          accountType: bid.accountType,
          companyName: bid.companyName || account?.company || '',
          logoUrl: internal ? INTERNAL_BID_COMPANY.logoUrl : getCrmCompanyLogoDisplayUrl(account),
          contactName: internal ? 'Internal team' : contactDisplayName(account),
          bids: [],
          outBids: [],
          inBids: [],
        });
      }
      const group = map.get(key);
      group.bids.push(bid);
      if (isBidOut(bid)) group.outBids.push(bid);
      else if (isBidIn(bid)) group.inBids.push(bid);
    });
    return [...map.values()].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
  }, [filtered, accountsById]);

  const selectedGroup = useMemo(
    () => (selectedGroupKey ? accountGroups.find((g) => g.key === selectedGroupKey) || null : null),
    [selectedGroupKey, accountGroups]
  );

  /** Headline counts across every bid in the current filter. */
  const metrics = useMemo(() => {
    const m = { total: filtered.length, out: 0, inReview: 0, published: 0, accepted: 0 };
    filtered.forEach((b) => {
      if (isBidOut(b)) m.out += 1;
      else if (b.pricingStatus === PRICING_STATUS.published) m.published += 1;
      else if (isBidIn(b)) m.inReview += 1;
      if (b.clientResponse?.status === CLIENT_RESPONSE_STATUS.accepted) m.accepted += 1;
    });
    return m;
  }, [filtered]);

  const hasActiveFilters =
    !!search.trim() || departmentFilter !== 'All' || accountTypeFilter !== 'All' || statusFilter !== 'All';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      <PortalBackground />
      <div className="relative w-full">
        {/* Header — same layout as CRM Tasks: centred title, add button on the right */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center w-full justify-center">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">Bids</h1>
          </div>
          <div className="flex items-center gap-4">
            <AddButton onClick={() => setShowNewBid(true)} title="New bid" aria-label="New bid" />
          </div>
        </div>

        {/* Search and filters — relative z-30 keeps the open dropdowns above the cards */}
        <div className="relative z-30 flex flex-col md:flex-row md:items-center gap-3 mb-5">
          <div className="relative w-full md:flex-1 md:min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bids by company or service…"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-10 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <TaskStyleFilter
              label="Department"
              value={departmentFilter}
              options={DEPARTMENT_FILTERS}
              onChange={setDepartmentFilter}
            />
            <TaskStyleFilter
              label="Type"
              value={accountTypeFilter}
              options={ACCOUNT_TYPE_FILTERS}
              onChange={setAccountTypeFilter}
            />
            <TaskStyleFilter
              label="Status"
              value={statusFilter}
              options={STATUS_FILTERS}
              onChange={setStatusFilter}
            />
          </div>
        </div>

        <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <CrmStatCard icon={ClipboardList} label="Total bids" value={metrics.total} tone="gray" />
          <CrmStatCard icon={Clock} label="Out for bid" value={metrics.out} tone="orange" />
          <CrmStatCard icon={Send} label="Priced / in review" value={metrics.inReview} tone="blue" />
          <CrmStatCard icon={CheckCircle2} label="Published" value={metrics.published} tone="green" />
          <CrmStatCard icon={ThumbsUp} label="Client accepted" value={metrics.accepted} tone="purple" />
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white/80 p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900/80">
            <Package className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400">
              {hasActiveFilters ? 'No bids match these filters.' : 'No bids have been sent out yet.'}
            </p>
          </div>
        ) : departmentFilter === 'All' ? (
          <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {accountGroups.map((group) => (
              <li key={group.key}>
                <button
                  type="button"
                  onClick={() => setSelectedGroupKey(group.key)}
                  className="flex h-full w-full flex-col rounded-2xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:border-red-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-red-800"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <CompanyLogo logoUrl={group.logoUrl} name={group.companyName} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="truncate font-semibold text-gray-900 dark:text-white">
                          {group.companyName || 'Unknown company'}
                        </h2>
                        <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {bidAccountTypeLabel(group.accountType)}
                        </span>
                      </div>
                      {group.contactName && (
                        <p className="truncate text-sm text-gray-500 dark:text-gray-400">{group.contactName}</p>
                      )}
                    </div>
                  </div>

                  <dl className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-amber-50 p-3 dark:bg-amber-900/20">
                      <dt className="text-xs font-medium uppercase text-amber-900 dark:text-amber-200">Out for bid</dt>
                      <dd className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-2xl font-bold leading-none text-amber-900 dark:text-amber-100">
                          {group.outBids.length}
                        </span>
                        {group.outBids.map((b) => (
                          <span key={b.id} title={b.name} className="text-lg leading-none">
                            {b.icon || '📦'}
                          </span>
                        ))}
                      </dd>
                    </div>
                    <div className="rounded-xl bg-blue-50 p-3 dark:bg-blue-900/20">
                      <dt className="text-xs font-medium uppercase text-blue-900 dark:text-blue-200">Priced</dt>
                      <dd className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-2xl font-bold leading-none text-blue-900 dark:text-blue-100">
                          {group.inBids.length}
                        </span>
                        {group.inBids.map((b) => (
                          <span key={b.id} title={b.name} className="text-lg leading-none">
                            {b.icon || '📦'}
                          </span>
                        ))}
                      </dd>
                    </div>
                  </dl>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="w-full min-w-[48rem] text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">Service</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">Company</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">Status</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">Sent out</th>
                  <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">Client quote</th>
                  <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">Internal cost</th>
                  <th scope="col" className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.map((bid) => {
                  const showQuote =
                    bid.pricingStatus === PRICING_STATUS.exec_ready || bid.pricingStatus === PRICING_STATUS.published;
                  const { marginPercent } = computePricingMetrics(bid.internalCost, bid.clientListPrice);

                  return (
                    <tr
                      key={bid.id}
                      onClick={() => navigate(`/crm/alacarte-bid/${bid.id}`)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2">
                          <span className="text-lg leading-none">{bid.icon || '📦'}</span>
                          <span className="font-medium text-gray-900 dark:text-white">{bid.name}</span>
                          {bid.isPackage && (
                            <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] uppercase text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">
                              Package
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {bid.companyName || 'Unknown company'}
                        </span>
                        <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {bidAccountTypeLabel(bid.accountType)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusPillClass(bid.pricingStatus)}`}
                        >
                          {BID_STATUS_LABELS[bid.pricingStatus] || bid.pricingStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDate(bid.createdAt)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                        {showQuote ? formatMoney(bid.clientListPrice) : 'Pending'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                        {formatMoney(bid.internalCost)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                        {marginPercent != null && Number.isFinite(marginPercent)
                          ? `${marginPercent.toFixed(1)}%`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedGroup && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setSelectedGroupKey(null)}
          role="presentation"
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedGroup.companyName || 'Account'} bids`}
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <CompanyLogo logoUrl={selectedGroup.logoUrl} name={selectedGroup.companyName} size="h-14 w-14" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedGroup.companyName || 'Unknown company'}
                    </h2>
                    <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {bidAccountTypeLabel(selectedGroup.accountType)}
                    </span>
                  </div>
                  {selectedGroup.contactName && (
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">{selectedGroup.contactName}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedGroupKey(null)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6 grid grid-cols-3 gap-3">
              <CrmStatCard icon={ClipboardList} label="Total" value={selectedGroup.bids.length} tone="gray" compact />
              <CrmStatCard icon={Clock} label="Out for bid" value={selectedGroup.outBids.length} tone="orange" compact />
              <CrmStatCard icon={Send} label="Priced" value={selectedGroup.inBids.length} tone="blue" compact />
            </div>

            <ul className="space-y-3">
              {selectedGroup.bids.map((bid) => {
                const showQuote =
                  bid.pricingStatus === PRICING_STATUS.exec_ready || bid.pricingStatus === PRICING_STATUS.published;
                const { marginPercent } = computePricingMetrics(bid.internalCost, bid.clientListPrice);

                return (
                  <li key={bid.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/crm/alacarte-bid/${bid.id}`)}
                      onKeyDown={(e) => {
                        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          navigate(`/crm/alacarte-bid/${bid.id}`);
                        }
                      }}
                      className={`w-full cursor-pointer rounded-xl border p-4 text-left transition ${
                        confirmDeleteId === bid.id
                          ? 'border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10'
                          : 'border-gray-200 hover:border-red-300 dark:border-gray-700 dark:hover:border-red-800'
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl leading-none">{bid.icon || '📦'}</span>
                          <div>
                            <p className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                              {bid.name}
                              {bid.isPackage && (
                                <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] uppercase text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">
                                  Package
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {bid.department} · Sent out {formatDate(bid.createdAt)}
                              {bid.isPackage ? ` · ${bid.packageItems.length} services` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                        <span className="flex flex-col items-end gap-1">
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusPillClass(bid.pricingStatus)}`}
                          >
                            {BID_STATUS_LABELS[bid.pricingStatus] || bid.pricingStatus}
                          </span>
                          {bid.clientResponse?.status && (
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${clientResponsePillClass(
                                bid.clientResponse.status
                              )}`}
                            >
                              Client: {CLIENT_RESPONSE_LABELS[bid.clientResponse.status]}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(bid.id);
                          }}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                          title="Delete bid"
                          aria-label={`Delete ${bid.name} bid`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        </div>
                      </div>

                      <dl className="mt-3 grid grid-cols-3 gap-3">
                        <div>
                          <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                            Client quote
                          </dt>
                          <dd className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                            {showQuote ? formatMoney(bid.clientListPrice) : 'Pending'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                            Internal cost
                          </dt>
                          <dd className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                            {formatMoney(bid.internalCost)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Margin</dt>
                          <dd className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                            {marginPercent != null && Number.isFinite(marginPercent)
                              ? `${marginPercent.toFixed(1)}%`
                              : '—'}
                          </dd>
                        </div>
                      </dl>

                      {confirmDeleteId === bid.id && (
                        <div
                          className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-white px-3 py-2.5 dark:border-red-900/60 dark:bg-gray-900"
                          onClick={(e) => e.stopPropagation()}
                          role="presentation"
                        >
                          <p className="text-sm text-red-700 dark:text-red-300">
                            Delete this bid permanently? This can't be undone.
                          </p>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId('')}
                              disabled={deletingId === bid.id}
                              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteBid(bid)}
                              disabled={deletingId === bid.id}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {deletingId === bid.id ? 'Deleting…' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {showNewBid && (
        <NewBidModal
          accounts={[...accountsById.values()]}
          bids={bids}
          onClose={() => setShowNewBid(false)}
          onCreated={async () => {
            setShowNewBid(false);
            await load({ silent: true });
          }}
        />
      )}
    </div>
  );
}
